/**
 * PayloadInput - Form + JSON tabs for Network Payload
 * Fixed schema: { tenantId, prompt } plus optional customerId (JSON tab only) — tenantId is
 * memory/customer pool (e.g. acme-corp), not the workspace id (TN-…).
 * Form tab: memory tenant, prompt; Attachments (staging under tmp, moved on execute)
 * JSON tab: raw JsonEditor
 */
import React, { useState, useEffect, useLayoutEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { getRestApiBearerToken } from '@/services/apiToken';
import SegmentedTabGroup from '../../ui/SegmentedTabGroup';
import JsonEditor from './JsonEditor';
import FileDropzone from './FileDropzone';
import { NETWORK_LOG_LEVELS } from '@/constants/networkLogLevel';
import { useAppStore } from '@/stores/useAppStore';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

const DEFAULT_PAYLOAD = { tenantId: '', prompt: '' };
const DEFAULT_JSON = '{\n  "tenantId": "",\n  "prompt": ""\n}';

/** Shapes the JSON string for the Form tab; omits customerId when unset (set via JSON if needed). */
function formStateToJsonObject(tenantId, prompt, customerId) {
  const o = { tenantId, prompt };
  if (String(customerId ?? '').trim()) o.customerId = String(customerId).trim();
  return o;
}

const stagingStorageKey = (networkId) => `stepflow.runPayload.stagingId.${networkId}`;
const attachmentsStorageKey = (networkId) => `stepflow.runPayload.attachments.${networkId}`;

function parsePayload(str) {
  try {
    const parsed = JSON.parse(str || '{}');
    return {
      tenantId: String(parsed.tenantId ?? ''),
      customerId: String(parsed.customerId ?? ''),
      prompt: String(parsed.prompt ?? '')
    };
  } catch {
    return null;
  }
}

const TAB_FORM = 0;
const TAB_JSON = 1;

/** 'inline' = upload strip inside the form scroll column; 'below-content' = full-width footer under the card body (matches Data panel). */
const PayloadInput = forwardRef(function PayloadInput(
  {
    value,
    onChange,
    disabled,
    logLevel,
    onLogLevelChange,
    networkSavedLogLevel,
    networkId,
    runtimeConfig,
    attachmentFooterPlacement = 'inline'
  },
  ref
) {
  const { user } = useAuth();
  const { addRunnerLog } = useAppStore();
  const [activeTab, setActiveTab] = useState(TAB_FORM);
  const [tenantId, setTenantId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [prompt, setPrompt] = useState('');
  const [jsonPayload, setJsonPayload] = useState(DEFAULT_JSON);
  const [testAttachments, setTestAttachments] = useState([]);
  const [attachmentStagingId, setAttachmentStagingId] = useState('');
  const [removingKey, setRemovingKey] = useState(null);

  useEffect(() => {
    if (value != null) {
      if (typeof value === 'string') {
        const parsed = parsePayload(value);
        if (parsed) {
          setTenantId(parsed.tenantId);
          setCustomerId(parsed.customerId);
          setPrompt(parsed.prompt);
          setJsonPayload(value);
        }
      } else if (typeof value === 'object') {
        setTenantId(String(value.tenantId ?? ''));
        setCustomerId(String(value.customerId ?? ''));
        setPrompt(String(value.prompt ?? ''));
        setJsonPayload(
          JSON.stringify(
            formStateToJsonObject(
              String(value.tenantId ?? ''),
              String(value.prompt ?? ''),
              String(value.customerId ?? '')
            ),
            null,
            2
          )
        );
      }
    }
  }, [value]);

  useLayoutEffect(() => {
    if (!networkId || networkId === 'new') {
      setAttachmentStagingId('');
      return;
    }
    try {
      let sid = sessionStorage.getItem(stagingStorageKey(networkId));
      if (!sid) {
        sid = crypto.randomUUID();
        sessionStorage.setItem(stagingStorageKey(networkId), sid);
      }
      setAttachmentStagingId(sid);
    } catch {
      setAttachmentStagingId(crypto.randomUUID());
    }
  }, [networkId]);

  useEffect(() => {
    if (!networkId || networkId === 'new') {
      setTestAttachments([]);
      return;
    }
    try {
      const raw = sessionStorage.getItem(attachmentsStorageKey(networkId));
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) setTestAttachments(arr);
        else setTestAttachments([]);
      } else {
        setTestAttachments([]);
      }
    } catch {
      setTestAttachments([]);
    }
  }, [networkId]);

  useEffect(() => {
    if (!networkId || networkId === 'new') return;
    try {
      sessionStorage.setItem(attachmentsStorageKey(networkId), JSON.stringify(testAttachments));
    } catch {
      /* ignore quota */
    }
  }, [networkId, testAttachments]);

  const handleTestUploadedAsset = useCallback(
    (asset) => {
      setTestAttachments((prev) => [...prev, asset]);
      addRunnerLog?.(`📎 Attachment staged: ${asset.filename}`);
    },
    [addRunnerLog]
  );

  const removeAttachment = useCallback(
    async (a) => {
      if (!networkId || disabled) return;
      const idToken = getRestApiBearerToken();
      if (!idToken) {
        addRunnerLog?.('❌ Sign in required to remove attachment');
        return;
      }
      setRemovingKey(a.s3Key);
      try {
        const res = await fetch(`${API_BASE_URL}/networks/${networkId}/delete-input-file`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ s3Key: a.s3Key })
        });
        let data = {};
        try {
          data = await res.json();
        } catch {
          /* no-op */
        }
        if (!res.ok) {
          addRunnerLog?.(`❌ Remove failed: ${data.error || res.statusText}`);
          return;
        }
        setTestAttachments((prev) => prev.filter((x) => x.s3Key !== a.s3Key));
        addRunnerLog?.(`🗑️ Removed attachment: ${a.filename}`);
      } catch (e) {
        addRunnerLog?.(`❌ Remove failed: ${e.message}`);
      } finally {
        setRemovingKey(null);
      }
    },
    [networkId, disabled, user?.idToken, addRunnerLog]
  );

  const rotateStagingAfterSuccessfulRun = useCallback(() => {
    if (!networkId || networkId === 'new') return;
    setTestAttachments([]);
    try {
      sessionStorage.removeItem(attachmentsStorageKey(networkId));
      const next = crypto.randomUUID();
      sessionStorage.setItem(stagingStorageKey(networkId), next);
      setAttachmentStagingId(next);
    } catch {
      setAttachmentStagingId(crypto.randomUUID());
    }
  }, [networkId]);

  const emitPayload = (payload) => {
    const obj = {
      tenantId: payload.tenantId ?? '',
      customerId: payload.customerId ?? '',
      prompt: payload.prompt ?? ''
    };
    onChange?.(obj);
  };

  const handleFormChange = (field, val) => {
    if (field === 'tenantId') {
      setTenantId(val);
      setJsonPayload(JSON.stringify(formStateToJsonObject(val, prompt, customerId), null, 2));
      emitPayload({ tenantId: val, customerId, prompt });
    } else {
      setPrompt(val);
      setJsonPayload(JSON.stringify(formStateToJsonObject(tenantId, val, customerId), null, 2));
      emitPayload({ tenantId, customerId, prompt: val });
    }
  };

  const handleJsonChange = (str) => {
    setJsonPayload(str);
    const parsed = parsePayload(str);
    if (parsed) {
      setTenantId(parsed.tenantId);
      setCustomerId(parsed.customerId);
      setPrompt(parsed.prompt);
      emitPayload(parsed);
    }
  };

  const getPayload = () => {
    if (activeTab === TAB_FORM) {
      return { tenantId, customerId, prompt };
    }
    const parsed = parsePayload(jsonPayload);
    return parsed ?? null;
  };

  const format = () => {
    try {
      const parsed = JSON.parse(jsonPayload || '{}');
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonPayload(formatted);
      const p = parsePayload(formatted);
      if (p) {
        setTenantId(p.tenantId);
        setCustomerId(p.customerId);
        setPrompt(p.prompt);
        emitPayload(p);
      }
    } catch {
      /* no-op */
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      getPayload,
      getTestAttachments: () => testAttachments,
      getAttachmentStagingId: () => attachmentStagingId,
      clearTestAttachments: () => setTestAttachments([]),
      rotateStagingAfterSuccessfulRun,
      format,
      isValidJson: () => {
        try {
          JSON.parse(jsonPayload || '{}');
          return true;
        } catch {
          return false;
        }
      }
    }),
    [
      testAttachments,
      jsonPayload,
      tenantId,
      customerId,
      prompt,
      activeTab,
      attachmentStagingId,
      rotateStagingAfterSuccessfulRun
    ]
  );

  const showLogLevel = logLevel != null && typeof onLogLevelChange === 'function';
  const showAttachments = Boolean(networkId && activeTab === TAB_FORM);

  const tagClass =
    'inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full max-w-[240px]';

  const showAttachmentStrip = Boolean(showAttachments && attachmentStagingId);
  const attachmentStrip = showAttachmentStrip ? (
    <div className="flex-shrink-0 p-3 border-t border-gray-100 space-y-2">
      <FileDropzone
        networkId={networkId}
        runtimeConfig={runtimeConfig}
        onUploadedAsset={handleTestUploadedAsset}
        compact
        compactMulti
        stagingId={attachmentStagingId}
      />
      {testAttachments.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          {testAttachments.map((a) => (
            <span key={a.s3Key} className={tagClass} title={a.filename}>
              <span className="truncate">{a.filename}</span>
              <button
                type="button"
                disabled={disabled || removingKey === a.s3Key}
                onClick={() => removeAttachment(a)}
                className="hover:text-blue-600 focus:outline-none shrink-0 disabled:opacity-40"
                aria-label={`Remove ${a.filename}`}
              >
                {removingKey === a.s3Key ? '…' : '×'}
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  ) : null;

  const tabsRow = (
    <div className="mb-3 flex-shrink-0 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <SegmentedTabGroup
        value={activeTab}
        onChange={setActiveTab}
        options={[
          { value: TAB_FORM, label: 'Form' },
          { value: TAB_JSON, label: 'JSON' }
        ]}
        ariaLabel="Payload view"
      />
      {showLogLevel && (
        <div className="flex items-center gap-2 min-w-0 ml-auto sm:ml-0">
          <label htmlFor="run-log-level" className="text-xs text-gray-600 whitespace-nowrap shrink-0">
            Log level <span className="text-gray-400 font-normal">(this run)</span>
          </label>
          <select
            id="run-log-level"
            value={logLevel}
            onChange={(e) => onLogLevelChange(e.target.value)}
            disabled={disabled}
            className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white text-gray-800 min-w-[7rem] max-w-[200px]"
          >
            {NETWORK_LOG_LEVELS.map((v) => (
              <option key={v} value={v}>
                {v}
                {networkSavedLogLevel != null && v === networkSavedLogLevel ? ' · network default' : ''}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );

  const mainBody = (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      {activeTab === TAB_FORM ? (
        <>
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-1">
            <div className="space-y-4 pb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Memory tenant <span className="text-gray-400 font-normal">(customer / pool)</span>
                </label>
                <input
                  type="text"
                  value={tenantId}
                  onChange={(e) => handleFormChange('tenantId', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g. acme-corp or tenant-001"
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div className="flex-1 min-h-0 flex flex-col">
                <label className="block text-xs font-medium text-gray-600 mb-1">Prompt</label>
                <textarea
                  value={prompt}
                  onChange={(e) => handleFormChange('prompt', e.target.value)}
                  disabled={disabled}
                  placeholder="Describe what you want the network to do..."
                  rows={6}
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none disabled:bg-gray-50 disabled:text-gray-500 min-h-[120px]"
                />
              </div>
            </div>
          </div>

          {attachmentFooterPlacement === 'inline' && attachmentStrip}
        </>
      ) : (
        <div className="h-full min-h-0">
          <JsonEditor value={jsonPayload} onChange={handleJsonChange} disabled={disabled} />
        </div>
      )}
    </div>
  );

  if (attachmentFooterPlacement === 'below-content') {
    return (
      <>
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden px-5 pt-4 pb-4">
          {tabsRow}
          {mainBody}
        </div>
        {attachmentStrip}
      </>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      {tabsRow}
      {mainBody}
    </div>
  );
});

export default PayloadInput;
export { parsePayload, DEFAULT_PAYLOAD, DEFAULT_JSON };
